export interface CsvRow {
    cells: string[]
    isHeader: boolean
}

class CsvTableService {
    /**
     * Parses a CSV string into rows of cells.
     * Handles quoted fields, escaped quotes (doubled quotes), and various line endings.
     * @param content - The CSV content as a string
     * @returns Array of CsvRow objects with cells and header flag
     */
    parseCsv(content: string): CsvRow[] {
        const rows: CsvRow[] = []
        let currentRow: string[] = []
        let currentCell = ''
        let inQuotes = false
        let i = 0

        while (i < content.length) {
            const char = content[i]
            const nextChar = content[i + 1]

            if (inQuotes) {
                if (char === '"') {
                    if (nextChar === '"') {
                        // Escaped quote (doubled quote)
                        currentCell += '"'
                        i += 2
                        continue
                    } else {
                        // End of quoted field
                        inQuotes = false
                        i++
                        continue
                    }
                } else {
                    currentCell += char
                    i++
                    continue
                }
            } else {
                if (char === '"') {
                    // Start of quoted field
                    inQuotes = true
                    i++
                    continue
                }

                if (char === ',') {
                    // End of cell
                    currentRow.push(currentCell)
                    currentCell = ''
                    i++
                    continue
                }

                if (char === '\n' || char === '\r') {
                    // End of row
                    currentRow.push(currentCell)
                    currentCell = ''
                    rows.push({
                        cells: currentRow,
                        isHeader: rows.length === 0
                    })
                    currentRow = []
                    // Handle \r\n
                    if (char === '\r' && nextChar === '\n') {
                        i += 2
                    } else {
                        i++
                    }
                    continue
                }

                currentCell += char
                i++
            }
        }

        // Handle last cell/row if content doesn't end with newline
        if (currentCell.length > 0 || currentRow.length > 0) {
            currentRow.push(currentCell)
            rows.push({
                cells: currentRow,
                isHeader: rows.length === 0
            })
        }

        return rows
    }

    getAfterAddRow(sourceText: string): string {
        const lines = sourceText.split('\n')

        if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
            return ''
        }

        const firstLine = lines[0]
        const columnCount = firstLine.split(',').length
        const newRow = Array(columnCount).fill('').join(',')

        return sourceText.endsWith('\n')
            ? sourceText + newRow
            : sourceText + '\n' + newRow
    }

    getAfterAddColumn(sourceText: string): string {
        const rows = this.parseCsv(sourceText)

        if (rows.length === 0) {
            return ''
        }

        const updatedRows = rows.map(row => ({
            ...row,
            cells: [...row.cells, '']
        }))

        return this.toCsvString(updatedRows)
    }

    getAfterDuplicateRow(sourceText: string, rowIndex: number): string {
        const rows = this.parseCsv(sourceText)

        if (rows.length === 0) {
            return ''
        }

        const clampedIndex = Math.max(0, Math.min(rowIndex, rows.length - 1))
        const duplicatedRow: CsvRow = {
            cells: [...rows[clampedIndex].cells],
            isHeader: false
        }

        const updatedRows = [
            ...rows.slice(0, clampedIndex + 1),
            duplicatedRow,
            ...rows.slice(clampedIndex + 1)
        ]

        return this.toCsvString(updatedRows)
    }

    getAfterDeleteRow(sourceText: string, rowIndex: number): string {
        const rows = this.parseCsv(sourceText)

        if (rows.length === 0 || rows.length === 1) {
            return sourceText
        }

        const clampedIndex = Math.max(0, Math.min(rowIndex, rows.length - 1))
        const updatedRows = [
            ...rows.slice(0, clampedIndex),
            ...rows.slice(clampedIndex + 1)
        ]

        return this.toCsvString(updatedRows)
    }

    private toCsvString(rows: CsvRow[]): string {
        const serializeCell = (cell: string): string => {
            if (cell.includes(',') || cell.includes('"') || cell.includes('\n') || cell.includes('\r')) {
                return '"' + cell.replace(/"/g, '""') + '"'
            }
            return cell
        }

        return rows.map(row => row.cells.map(serializeCell).join(',')).join('\n')
    }
}

export const csvTableService = new CsvTableService()
